/**
 * Authenticated Layout
 *
 * Persistent authentication shell that wraps all protected pages.
 * This component NEVER unmounts during navigation, maintaining:
 * - MSAL authentication state
 * - Firebase connection
 * - User permissions
 * - Zustand store state
 *
 * This is the key to achieving instant page transitions.
 */

import { useEffect, useCallback, useMemo, useState } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useMicrosoftAuth, useFirebaseInit, useUserPermissionsListener, useLocationsListener, useDivisionsListener, useVersionCheck, UpdateNotificationBanner, Toast, useToast, cleanupCacheBustParam, usePresenceTracker, useTelemetryStore, useFCMToken, useServiceWorkerInit, useForegroundMessaging, useVisibilityReconnect, useAppConfig, useAnnouncementsListener, useAnnouncements, AnnouncementsPopup, MaintenanceOverlay, useRTDBManagerInit, useNotificationStore, useNotifications, NotificationProvider, NotificationHistoryModal, NotificationToastStack, resolveNotificationUrl, isPersonalNotificationSource, UserSettingsModal } from '@/shared';
import { useMyQuizAssignments } from '@/quizzes-react/hooks/useMyQuizAssignments';
import { useMyScenarioSteps } from '@/quizzes-react/hooks/useMyScenarioSteps';
import { useGuidesListener } from '@/shared/hooks/useGuides';
import { useFirebaseAuthRestore } from '@/shared/hooks/useFirebaseAuthRestore';
import { shouldDropQueuedNotification } from '@/shared/utils/notificationQueue';
import type { NotificationItem } from '@/types/notification.types';
import type { NotificationToastItem } from '@/shared/store/notificationStore';

// Debug utility for verifying custom claims - exposes window.checkClaimsStatus()
import '@/shared/utils/debugClaims';
import { getAuth } from 'firebase/auth';
import { getApps } from 'firebase/app';
import { useFirebaseStore } from '@/shared';
import { LoadingSpinner } from '@/shared';
import { useRef } from 'react';

export default function AuthenticatedLayout() {
  // Check for persisted Firebase session BEFORE the MSAL gate
  // This resolves in ~50-100ms and can restore sessions without MSAL
  useFirebaseAuthRestore();

  // MSAL authentication (initialized once, persists across navigation)
  const { isAuthenticated, isLoading: isAuthLoading, account, instance } = useMicrosoftAuth();
  const { currentUser, isConnected, isUserLoading, messaging, authSource, isAuthChecking } = useFirebaseStore();

  // True if user is authenticated via either MSAL or Firebase persistence
  const isEffectivelyAuthenticated = isAuthenticated || authSource === 'firebase_restored';

  // Global toast notifications
  const { toast, hideToast } = useToast();

  // Get user email from Firebase Auth — the single source of truth.
  // Firebase Auth's currentUser.uid is the lowercase email, set by the backend
  // custom token generator (auth.py). This is available as soon as Firebase
  // restores the persisted session, without waiting for MSAL.
  // Guard: getAuth() throws if no Firebase app is initialized yet (first render
  // before useFirebaseAuthRestore's effect runs). isConnected triggers re-render
  // once Firebase is ready, at which point currentUser is available.
  const firebaseUser = getApps().length > 0 ? getAuth().currentUser : null;
  const normalizedEmail = firebaseUser?.uid || null;

  // Initialize Firebase connection (only once per session)
  useFirebaseInit({
    msalAccount: account,
    isAuthenticated,
    msalInstance: instance
  });

  // Set up real-time listener for user permissions (SINGLE SOURCE OF TRUTH)
  // This replaces the old useUserRole hook - it handles:
  // - Initial user document loading
  // - Real-time permission updates
  // - Creating default user doc if needed
  // - Populating Zustand store with all user data (including fcmTokens)
  useUserPermissionsListener(normalizedEmail, account?.name || null, isConnected);

  // Initialize service worker for push notifications (runs once on app load)
  // This ensures the SW stays registered even when the page is closed
  useServiceWorkerInit();

  // Auto-refresh FCM token on page load (if permission already granted)
  // This ensures the backend always has a valid token for push delivery
  useFCMToken();

  // Listen for FCM messages when app is in foreground
  // Shows in-app toasts and system notifications (mobile only)
  useForegroundMessaging();

  // Handle tab visibility changes for Firestore reconnection
  // When tab wakes from sleep, forces listeners to re-subscribe for fresh data
  useVisibilityReconnect();

  // Initialize persistent RTDB listener managers for rentals/stamps boards
  // This enables lazy-loading: listeners stay active across page navigation
  // and location changes, avoiding expensive full re-syncs
  useRTDBManagerInit();

  // NOTE: useRolesListener() was removed as an optimization (Section 1 of Firestore Optimization Plan)
  // Roles data is only needed in Admin Panel, where RolesSection.tsx has its own listener.
  // Regular users don't need roles data - roles are compiled to pageAccess when admin saves.

  // Set up real-time listener for location config (name, address changes)
  // Pass isConnected to prevent false "not found" warnings before auth is ready
  useLocationsListener(isConnected);

  // Set up real-time listener for division config (division definitions)
  // Pass isConnected to prevent false "not found" warnings before auth is ready
  useDivisionsListener(isConnected);

  // Version checking (background + route change detection)
  const { hasUpdate, performUpdate, checkOnRouteChange } = useVersionCheck();
  const location = useLocation();
  const navigate = useNavigate();

  // Telemetry: track current page for Firestore operation attribution
  const setCurrentPage = useTelemetryStore((state) => state.setCurrentPage);

  // Session tracking for admin visibility (sends lightweight heartbeats)
  usePresenceTracker();

  // App config listener (force reload, maintenance mode, etc.)
  // Pass isConnected to prevent false "not found" warnings before auth is ready
  const { maintenanceMode, maintenanceMessage } = useAppConfig(isConnected);

  // Check if user is admin (exempt from maintenance mode to be able to turn it off)
  const isAdmin = currentUser?.role === 'admin';

  // Announcements system - set up the SINGLE Firestore listener here
  // All other components read from the store populated by this listener
  // Pass isConnected to prevent listener setup before auth is ready
  useAnnouncementsListener(isConnected);
  const { popupAnnouncements, dismissAll } = useAnnouncements();

  // Page guides system - set up the SINGLE Firestore listener here
  // All PageContainer instances read from the guidesStore populated by this listener
  useGuidesListener(isConnected);

  // FCM token management for push notifications
  const { registerFCMToken, permissionStatus } = useFCMToken();
  const hasRefreshedToken = useRef(false);

  // Auto-refresh FCM token on app load if user has notifications enabled
  // This handles cases like PWA reinstall where the token changes
  // Bug 8: Added debounce to prevent rapid-fire registration during app initialization
  // Bug 9: Now waits for Firebase messaging to be initialized (race condition fix)
  // Bug 10: Removed cleanup function that was cancelling the timeout during isUserLoading toggle
  useEffect(() => {
    // Only run once per session, after user data AND messaging are fully loaded
    if (hasRefreshedToken.current || !currentUser || isUserLoading) return;

    // Wait for Firebase messaging to be initialized
    if (!messaging) return;

    // Check if user has notifications enabled in their settings
    const hasNotificationsEnabled = currentUser.notificationSettings?.enabled === true;

    // Check if browser has granted permission
    const hasBrowserPermission = permissionStatus === 'granted';

    if (hasNotificationsEnabled && hasBrowserPermission) {
      hasRefreshedToken.current = true;
      // Small debounce to ensure store is fully propagated
      // NOTE: We intentionally don't return a cleanup function here.
      // The hasRefreshedToken ref prevents duplicate calls, and returning
      // a cleanup would cancel the timeout if the effect re-runs before 100ms
      // (which happens when isUserLoading toggles during initial load).
      setTimeout(() => {
        // Silently refresh token in background - no UI feedback needed
        registerFCMToken().catch(() => {
          // Silently ignore errors - user can manually re-enable if needed
        });
      }, 100);
    }
  }, [currentUser, isUserLoading, permissionStatus, registerFCMToken, messaging]);

  // Clean up cache-bust parameter from URL after force reload
  // This removes the ugly ?_t=timestamp from the address bar
  useEffect(() => {
    cleanupCacheBustParam();
  }, []);

  // Check for version mismatch on route changes
  useEffect(() => {
    if (isEffectivelyAuthenticated) {
      checkOnRouteChange();
    }
  }, [location, isEffectivelyAuthenticated, checkOnRouteChange]);

  // Update telemetry store with current page for Firestore operation attribution
  useEffect(() => {
    setCurrentPage(location.pathname);
  }, [location.pathname, setCurrentPage]);

  // Listen for service worker messages (for notification click navigation on iOS PWA)
  // When a notification is clicked and the SW can't directly navigate, it posts a message
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.targetPath) {
        // Navigate to the target path from the notification
        navigate(event.data.targetPath);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [navigate]);

  // Notification toast TTL cleanup - runs every minute to remove stale toasts
  const cleanupStaleToasts = useNotificationStore((state) => state.cleanupStaleToasts);
  useEffect(() => {
    // Run cleanup every minute
    const interval = setInterval(() => {
      cleanupStaleToasts();
    }, 60000);

    return () => clearInterval(interval);
  }, [cleanupStaleToasts]);

  // Offline queue processing - send queued notifications when back online
  const { getOfflineQueue, removeFromOfflineQueue } = useNotificationStore();
  useEffect(() => {
    const processOfflineQueue = async () => {
      const queue = getOfflineQueue();
      if (queue.length === 0) return;

      // Process each queued notification
      for (const item of queue) {
        try {
          const auth = await import('firebase/auth').then((m) => m.getAuth());
          const user = auth.currentUser;
          if (!user) continue;

          const token = await user.getIdToken();
          const response = await fetch('/api/notifications/send', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(item.payload),
          });

          // Drop on success and on a permanent 4xx (the backend rejected it
          // on validation/permissions — a replay can never succeed).
          if (shouldDropQueuedNotification(response.status)) {
            removeFromOfflineQueue(item.timestamp);
          }
        } catch {
          // Keep in queue for next online event
        }
      }
    };

    const handleOnline = () => {
      processOfflineQueue();
    };

    window.addEventListener('online', handleOnline);

    // Also process on mount if already online (handles page refresh while online)
    if (navigator.onLine) {
      processOfflineQueue();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [getOfflineQueue, removeFromOfflineQueue]);

  // NOTE: Zustand store is now updated by useUserPermissionsListener (single source of truth)
  // No manual store update needed here

  // === Global Notification System ===
  // Subscribe to ALL notifications globally (global=true fetches all roles)
  // User's notification settings (per-page in-app/push toggles) filter what they see
  const { addToast, removeToast, clearToasts, toasts: notificationToasts, isToastDismissed } = useNotificationStore();
  const { selectedLocation, setSelectedLocation } = useFirebaseStore();

  // User settings modal for notification preferences (opened from bell dropdown)
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  // Notification history modal (opened from bell dropdown)
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  // Subscribe to notifications globally
  // Returned notifications already pass the shared visibility rule
  // (settings + page access; utils/notificationVisibility)
  const {
    notifications,
    unreadCount: notificationUnreadCount,
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
  } = useNotifications({
    global: true, // Fetch all roles (yard, sales, all)
    enableBrowserNotifications: true,
    enabled: isConnected && !!currentUser,
    // NOTE: We intentionally don't use onNewNotification here to add toasts.
    // Foreground toasts are handled by useForegroundMessaging (FCM push handler).
    // Using both would cause duplicate toasts when the app is in the foreground.
    // The useNotifications hook is still used for the notification bell/inbox.
  });

  // Subscribe to the current user's quiz assignments and open scenario steps once per
  // session (feeds the sidebar "My Quizzes" link and the home banner via myQuizzesStore)
  useMyQuizAssignments();
  useMyScenarioSteps();

  // Sync unread Firestore notifications to toast stack
  // This ensures users see toasts for notifications they missed while away
  // Runs whenever notifications change (including when returning to a suspended tab)
  // Uses a Set to track which notification IDs we've already attempted to sync
  const syncedNotificationIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!notifications || notifications.length === 0) return;
    if (!currentUser?.email) return;

    // Filter to unread notifications only
    const unreadNotifications = notifications.filter(
      (n) => !n.readBy?.includes(currentUser.email!)
    );

    // Add each unread notification as a toast (if not already processed)
    for (const notification of unreadNotifications) {
      // Skip if we've already attempted to sync this notification
      if (syncedNotificationIds.current.has(notification.id)) continue;

      // Mark as processed (even if dismissed, so we don't check again)
      syncedNotificationIds.current.add(notification.id);

      // Skip if already dismissed by user
      if (isToastDismissed(notification.id)) continue;

      // Convert Firestore notification to toast format
      // Use current timestamp so TTL starts from now (not original creation time)
      addToast({
        id: notification.id,
        type: notification.type,
        title: notification.title,
        body: notification.body,
        locationId: notification.locationId,
        // Personal toasts carry their url + a flag so a click can route/mark-read without the bell list
        metadata: notification.source === 'personal'
          ? { ...notification.metadata, personal: 'true', ...(notification.url ? { url: notification.url } : {}) }
          : notification.metadata,
        actionBy: notification.metadata?.actionBy,
        actionByName: notification.metadata?.actionByName,
        // timestamp intentionally omitted - addToast will use Date.now()
      });
    }
  }, [notifications, currentUser?.email, isToastDismissed, addToast]);

  // Handle notification click from bell dropdown - navigates to target page
  const handleNotificationBellClick = useCallback((notification: NotificationItem) => {
    const isPersonal = notification.source === 'personal';
    markNotificationAsRead(notification.id, isPersonal ? 'personal' : undefined);
    // Switch to the notification's location if different from current
    // (personal notifications are not location-scoped: never switch)
    if (!isPersonal && notification.locationId && notification.locationId !== selectedLocation) {
      setSelectedLocation(notification.locationId);
    }
    const url = resolveNotificationUrl(notification);
    if (url) {
      if (!location.pathname.startsWith(url.split('?')[0])) {
        navigate(url);
      } else {
        navigate(url, { replace: true });
      }
    }
  }, [markNotificationAsRead, navigate, location.pathname, selectedLocation, setSelectedLocation]);

  // Handle notification toast click - removes toast and navigates
  const handleNotificationToastClick = useCallback((toast: NotificationToastItem) => {
    removeToast(toast.id);
    const isPersonal = isPersonalNotificationSource(toast);
    markNotificationAsRead(toast.id, isPersonal ? 'personal' : undefined);
    // Switch to the toast's location if different from current (never for personal)
    if (!isPersonal && toast.locationId && toast.locationId !== selectedLocation) {
      setSelectedLocation(toast.locationId);
    }
    const url = resolveNotificationUrl(toast);
    if (url) {
      if (!location.pathname.startsWith(url.split('?')[0])) {
        navigate(url);
      } else {
        navigate(url, { replace: true });
      }
    }
  }, [removeToast, markNotificationAsRead, navigate, location.pathname, selectedLocation, setSelectedLocation]);

  // Handle clear all toasts across all locations
  const handleClearAllToasts = useCallback(() => {
    clearToasts();
  }, [clearToasts]);

  // Memoize notification context value to prevent unnecessary re-renders
  const notificationContextValue = useMemo(() => ({
    notifications,
    unreadCount: notificationUnreadCount,
    markAsRead: markNotificationAsRead,
    markAllAsRead: markAllNotificationsAsRead,
    isAvailable: isConnected && !!currentUser,
    // Additional handlers for PageContainer to use
    onNotificationClick: handleNotificationBellClick,
    onOpenSettings: () => setIsSettingsModalOpen(true),
    onOpenHistory: () => setIsHistoryModalOpen(true),
  }), [
    notifications,
    notificationUnreadCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    isConnected,
    currentUser,
    handleNotificationBellClick
  ]);

  // OPTIMIZATION: If we have cached user data, skip loading screen
  // normalizedEmail comes from Firebase Auth (available after restore or MSAL sign-in)
  const hasValidCache = currentUser && (
    normalizedEmail === currentUser.email ||
    authSource === 'firebase_restored'
  );

  // Show loading spinner during initial auth check (MSAL loading OR Firebase persistence check)
  if (isAuthLoading || isAuthChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-muted">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  // Redirect to login if not authenticated via either MSAL or Firebase persistence
  if (!isEffectivelyAuthenticated) {
    // Save current path for redirect after login
    const currentPath = window.location.pathname + window.location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(currentPath)}`} replace />;
  }

  // Show loading spinner while fetching user data ONLY if we don't have cached data
  if (isUserLoading && !hasValidCache) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-muted">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  // User is authenticated and permissions are loaded - render the app shell
  return (
    <NotificationProvider value={notificationContextValue}>
      {/* Update notification banner - shown when new version available */}
      {hasUpdate && <UpdateNotificationBanner onRefresh={performUpdate} />}

      {/* Main content area - child routes render here */}
      <Outlet />

      {/* Global notification toast stack - shows on all pages */}
      <NotificationToastStack
        toasts={notificationToasts}
        onDismiss={(id) => removeToast(id)}
        onClick={handleNotificationToastClick}
        onClearAll={handleClearAllToasts}
      />

      {/* Global toast notifications (success/error messages) - shown from anywhere in the app */}
      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        type={toast.type}
        onClose={hideToast}
        action={toast.action}
        position={toast.position}
      />

      {/* User settings modal for notification preferences */}
      <UserSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />

      {/* Notification history modal (opened from bell dropdown) */}
      <NotificationHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        onNotificationClick={handleNotificationBellClick}
      />

      {/* Announcements popup - auto-shows unread announcements */}
      <AnnouncementsPopup
        announcements={popupAnnouncements}
        onDismiss={dismissAll}
      />

      {/* Maintenance mode overlay - blocks all interaction (admins exempt) */}
      <MaintenanceOverlay
        isActive={maintenanceMode && !isAdmin}
        message={maintenanceMessage}
      />
    </NotificationProvider>
  );
}
